import { errorToast } from "@/v2-ui/toast"
import {
  type ConfirmButtonProps,
  ConfirmButton,
} from "@/v2-backoffice-client/components/button"
import { useUserSharingFlagQuery } from "@/v2-backoffice-client/user/sharingFlag/user.sharingFlag.query"
import { useSetUserSharingFlagMutation } from "@/v2-backoffice-client/user/sharingFlag/user.sharingFlag.mutation"

const ENFORCED_CONTENT =
  "Remove the single-active-session limit so the user can stay signed in on several devices."
const UNENFORCED_CONTENT =
  "Limit this user to one active session. On a second device they're asked to log out the other one."

const ENFORCED_CONFIRM =
  "Remove the single-active-session flag from this user? They will be able to sign in on multiple devices again."
const UNENFORCED_CONFIRM =
  "Flag this user for single active session? On a second device they will get a choice screen to log out the other session."

export type UserSharingFlagButtonProps = Omit<
  ConfirmButtonProps,
  "onConfirm" | "content" | "confirmContent"
> & {
  userId: number
  onCompleted?(): void
}

// Toggles the account-sharing flag (single-active-session enforcement). Owns
// the read as well as the write because the flag decides the button's own
// label and copy, and because setUserSharingFlag returns a row keyed by
// UserId rather than Id — Apollo can't normalise it into the cached query, so
// the current value has to be refetched rather than merged.
function UserSharingFlagButton(props: UserSharingFlagButtonProps) {
  const { userId, onCompleted, children, ...buttonProps } = props

  const { data, refetch } = useUserSharingFlagQuery({ variables: { userId } })
  const [setFlag, { loading, error }] = useSetUserSharingFlagMutation()
  errorToast(error, { title: "Failed to update sharing flag" })

  const isEnforced = Boolean(data?.getUserSharingFlag?.IsEnforced)

  return (
    <ConfirmButton
      variant="warning"
      iconSrc="flag"
      content={isEnforced ? ENFORCED_CONTENT : UNENFORCED_CONTENT}
      confirmContent={isEnforced ? ENFORCED_CONFIRM : UNENFORCED_CONFIRM}
      confirmVariant="warning"
      isLoading={loading}
      onConfirm={() => {
        setFlag({
          variables: { userId, enforce: !isEnforced, reason: null },
        })
          .then(() => refetch())
          .then(() => onCompleted?.())
          .catch(() => {})
      }}
      {...buttonProps}
    >
      {children ?? (isEnforced ? "Unflag Login" : "Flag Login")}
    </ConfirmButton>
  )
}

export default UserSharingFlagButton
